/**
 * SMS Counter Utility
 *
 * Pure functions for SMS character counting, encoding detection, and segment calculation. No Angular dependency — can
 * be used in any context.
 *
 * SMS encoding rules:
 *
 * - GSM-7: Standard Latin charset → 160 chars/single, 153 chars/segment
 * - Unicode: Any non-GSM character (emoji, CJK, Arabic…) → 70 chars/single, 67 chars/segment
 * - A single Unicode character switches the ENTIRE message to Unicode encoding
 */

/**
 * GSM-7 Basic Character Set + Extension Table
 *
 * @see https://en.wikipedia.org/wiki/GSM_03.38
 */
const GSM7_BASIC = new Set(
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./' +
    '0123456789:;<=>?¡' +
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ' +
    'ÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
);

/** Characters in GSM-7 extension table (each costs 2 chars: escape + char) */
const GSM7_EXTENSION = new Set(['^', '{', '}', '\\', '[', '~', ']', '|', '€']);

/** Check if every character in the text is within the GSM-7 charset. */
export function isGsm7(text: string): boolean {
  return [...text].every((ch) => GSM7_BASIC.has(ch) || GSM7_EXTENSION.has(ch));
}

/** Count the effective GSM-7 length (extension chars count as 2). Returns the raw string length if not GSM-7. */
export function gsm7Length(text: string): number {
  let len = 0;
  for (const ch of text) {
    len += GSM7_EXTENSION.has(ch) ? 2 : 1;
  }
  return len;
}

/** SMS statistics for a given text. */
export interface SmsStats {
  /** Current character count (GSM-7 effective length or Unicode code point count) */
  len: number;
  /** Number of SMS segments required */
  segments: number;
  /** True = GSM-7 encoding, false = Unicode encoding */
  gsm: boolean;
  /** Max characters for a single-segment SMS (160 or 70) */
  singleMax: number;
  /** Max characters per segment in multi-part SMS (153 or 67) */
  multiMax: number;
  /** Configured maximum segments */
  maxSegments: number;
  /** Absolute character limit = multiMax × maxSegments */
  hardCap: number;
  /** Characters remaining before reaching the hard cap */
  remaining: number;
}

/** Calculate SMS statistics for the given text. */
export function getSmsStats(text: string, maxSegments = 10): SmsStats {
  const gsm = text.length === 0 || isGsm7(text);
  const len = gsm ? gsm7Length(text) : [...text].length;
  const singleMax = gsm ? 160 : 70;
  const multiMax = gsm ? 153 : 67;
  const hardCap = multiMax * maxSegments;

  let segments: number;

  if (len === 0) {
    segments = 1;
  } else if (len <= singleMax) {
    segments = 1;
  } else {
    segments = Math.min(Math.ceil(len / multiMax), maxSegments);
  }

  const remaining = len <= singleMax ? singleMax - len : Math.max(0, hardCap - len);

  return { len, segments, gsm, singleMax, multiMax, maxSegments, hardCap, remaining };
}

/**
 * Truncate text to the hard cap for the current encoding. Must be called on every input event — the cap can shrink
 * mid-session if the user adds a Unicode character (e.g. an emoji).
 */
export function enforceSmsCap(text: string, maxSegments = 10): string {
  const gsm = isGsm7(text);
  const multiMax = gsm ? 153 : 67;
  const hardCap = multiMax * maxSegments;

  if (gsm) {
    // For GSM-7, we need to count effective length (extensions = 2)
    let effectiveLen = 0;
    let cutIndex = text.length;
    for (let i = 0; i < text.length; i++) {
      effectiveLen += GSM7_EXTENSION.has(text[i]) ? 2 : 1;
      if (effectiveLen > hardCap) {
        cutIndex = i;
        break;
      }
    }
    return text.slice(0, cutIndex);
  } else {
    // For Unicode, count code points
    const codePoints = [...text];
    return codePoints.length > hardCap ? codePoints.slice(0, hardCap).join('') : text;
  }
}
